import { NgModule } from '@angular/core';
import { Data, Route, RouterModule } from '@angular/router';
import { RequiredPermissions } from '@models/authorization/permission.model';
import { RecordingPreviewComponent } from './app-pages/marketing/recordings/preview/recording-preview.component';
import { NewsArticlePreviewComponent } from './app-pages/news-articles/preview/news-article-preview.component';
import { AuthenticatedTemplateComponent } from './components/authenticated-template/authenticated-template.component';
import { AuthGuard } from './guards/auth.guard';
import { GuidGuard } from './guards/guid.guard';
import { PermissionsGuard } from './guards/permissions.guard';

export interface AuthorizedRoute extends Route {
  data?: AuthorizedRouteData;
  children?: AuthorizedRoute[];
}

export interface AuthorizedRouteData extends Data {
  permissions?: RequiredPermissions;
  pageTitle?: string;
}

const authorizedRoutes: AuthorizedRoute[] = [
  {
    path: 'page',
    loadChildren: () => import('./app-pages/page/page.module').then(m => m.PageModule),
  },
  {
    path: 'users',
    loadChildren: () => import('./app-pages/users/users.routes').then(m => m.getUserRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Users',
      permissions: {
        menuAccess: ['Portal.Admin.Users.MenuAccess'],
      },
    },
  },
  {
    path: 'partners',
    loadChildren: () => import('./app-pages/partners/partners.routes').then(m => m.getPartnersRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Partners',
      permissions: {
        menuAccess: ['Portal.Admin.Partners.MenuAccess'],
        oneAtLeast: ['Portal.Admin.Partners.Read'],
      },
    },
  },
  {
    path: 'agreements',
    loadChildren: () => import('./app-pages/agreements/agreements.routes').then(m => m.getAgreementsRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Agreements',
      permissions: {
        menuAccess: ['Portal.Admin.Agreements.MenuAccess'],
        oneAtLeast: ['Portal.Admin.Agreements.Read'],
      },
    },
  },
  {
    path: 'permissions',
    loadChildren: () => import('./app-pages/authorization/permissions/permissions.routes').then(m => m.getPermissionsRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Permissions',
      permissions: {
        menuAccess: ['Portal.Admin.Authorization.Permissions.MenuAccess'],
      },
    },
  },
  {
    path: 'roles',
    loadChildren: () => import('./app-pages/authorization/roles/roles.routes').then(m => m.getRolesRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Roles',
      permissions: {
        menuAccess: ['Portal.Admin.Authorization.Roles.MenuAccess'],
      },
    },
  },
  {
    path: 'role-groups',
    loadChildren: () => import('./app-pages/authorization/role-groups/role-groups.routes').then(m => m.getRoleGroupsRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Role groups',
      permissions: {
        menuAccess: ['Portal.Admin.Authorization.RoleGroups.MenuAccess'],
      },
    },
  },
  {
    path: 'import-changes',
    loadChildren: () => import('./app-pages/authorization/import-changes/import-changes.routes').then(m => m.getImportChangesRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Import changes',
      permissions: {
        menuAccess: ['Portal.Admin.Authorization.ImportChanges.MenuAccess'],
      },
    },
  },
  {
    path: 'common-data',
    children: [
      {
        path: 'common-data',
        loadChildren: () => import('./app-pages/common-data/common-data/common-data.routes').then(m => m.getCommonDataRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Common data',
          permissions: {
            menuAccess: ['Portal.Admin.CommonData.MenuAccess'],
          },
        },
      },
      {
        path: 'regions',
        loadChildren: () => import('./app-pages/common-data/regions/regions.routes').then(m => m.getRegionsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Regions',
          permissions: {
            menuAccess: ['Portal.Admin.CommonData.Regions.MenuAccess'],
          },
        },
      },
      {
        path: 'membership-levels',
        loadChildren: () =>
          import('./app-pages/common-data/membership-levels/membership-levels.routes').then(m => m.getMembershipLevelsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Membership levels',
          permissions: {
            menuAccess: ['Portal.Admin.CommonData.MembershipLevels.MenuAccess'],
          },
        },
      },
      {
        path: 'languages',
        loadChildren: () => import('./app-pages/common-data/languages/languages.routes').then(m => m.getLanguagesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Languages',
          permissions: {
            menuAccess: ['Portal.Admin.CommonData.Languages.MenuAccess'],
          },
        },
      },
      {
        path: 'countries',
        loadChildren: () => import('./app-pages/common-data/countries/countries.routes').then(m => m.getCountriesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Countries',
          permissions: {
            menuAccess: ['Portal.Admin.CommonData.Countries.MenuAccess'],
          },
        },
      },
    ],
  },
  {
    path: 'actions/logs',
    loadChildren: () => import('./app-pages/actions/action-logs/action-logs.routes').then(m => m.getActionLogsRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Action logs',
      permissions: {
        menuAccess: ['Portal.Admin.Actions.Logs.MenuAccess'],
      },
    },
  },
  {
    path: 'actions/types',
    loadChildren: () => import('./app-pages/actions/action-types/action-types.routes').then(m => m.getActionTypesRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Action types',
      permissions: {
        menuAccess: ['Portal.Admin.Actions.Types.MenuAccess'],
      },
    },
  },
  {
    path: 'marketing',
    children: [
      { path: '', redirectTo: 'announcements', pathMatch: 'full' },
      {
        path: 'announcements',
        loadChildren: () => import('./app-pages/marketing/announcements/announcements.routes').then(m => m.getAnnouncementsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Announcements',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.Announcements.MenuAccess'],
          },
        },
      },
      {
        path: 'documents',
        loadChildren: () => import('./app-pages/documents/documents.routes').then(m => m.getDocumentsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Documents',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.Documents.MenuAccess'],
          },
        },
      },
      {
        path: 'topics',
        loadChildren: () => import('./app-pages/marketing/topics/topics.routes').then(m => m.getTopicsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Topics',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.Topics.MenuAccess'],
          },
        },
      },
      {
        path: 'field-experts',
        loadChildren: () => import('./app-pages/marketing/field-experts/field-experts.routes').then(m => m.getFieldExpertsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Field experts',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.FieldExperts.MenuAccess'],
          },
        },
      },
      {
        path: 'news-articles',
        loadChildren: () => import('./app-pages/news-articles/news-articles.routes').then(m => m.getNewsArticlesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'News',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.NewsArticles.MenuAccess'],
          },
        },
      },
      {
        path: 'news-articles-comments',
        loadChildren: () => import('./app-pages/marketing/comments/comments.routes').then(m => m.getCommentsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Comments',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.Comments.MenuAccess'],
          },
        },
      },
      {
        path: 'communities',
        loadChildren: () => import('./app-pages/communities/communities.routes').then(m => m.getCommunitiesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Communities',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.Communities.MenuAccess'],
          },
        },
      },
      {
        path: 'categories',
        loadChildren: () => import('./app-pages/marketing/categories/categories.routes').then(m => m.getCategoriesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Categories',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.Categories.MenuAccess'],
          },
        },
      },
      {
        path: 'events',
        loadChildren: () => import('./app-pages/marketing/events/events.routes').then(m => m.getEventsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Events',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.Events.MenuAccess'],
          },
        },
      },
      {
        path: 'recordings',
        loadChildren: () => import('./app-pages/marketing/recordings/recordings.routes').then(m => m.getRecordingsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Recordings',
          permissions: {
            menuAccess: ['Portal.Admin.Marketing.Recordings.MenuAccess'],
          },
        },
      },
    ],
  },
  {
    path: 'file-management',
    children: [
      { path: '', redirectTo: 'branches', pathMatch: 'full' },
      {
        path: 'branches',
        loadChildren: () => import('./app-pages/file-management/branches/branches.routes').then(m => m.getBranchesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Branches',
          permissions: {
            menuAccess: ['Portal.Admin.FileManagement.Branches.MenuAccess'],
          },
        },
      },
      {
        path: 'files',
        loadChildren: () => import('./app-pages/file-management/files/files.routes').then(m => m.getFilesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Files',
          permissions: {
            menuAccess: ['Portal.Admin.FileManagement.Files.MenuAccess'],
          },
        },
      },
    ],
  },
  {
    path: 'products',
    loadChildren: () => import('./app-pages/products-orders/products/products.routes').then(m => m.getProductsRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Products',
      permissions: {
        menuAccess: ['Portal.Admin.ProductsAndOrders.Products.MenuAccess'],
      },
    },
  },
  {
    path: 'customer-reports',
    loadChildren: () =>
      import('./app-pages/products-orders/customer-reports/customer-reports.routes').then(m => m.getCustomerReportsRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Customer reports',
      permissions: {
        menuAccess: ['Portal.Admin.ProductsAndOrders.CustomerReports.MenuAccess'],
      },
    },
  },
  {
    path: 'reported-customers',
    loadChildren: () =>
      import('./app-pages/products-orders/reported-customers/reported-customers.routes').then(m => m.getReportedCustomersRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Reported customers',
      permissions: {
        menuAccess: ['Portal.Admin.ProductsAndOrders.ReportedCustomers.MenuAccess'],
      },
    },
  },
  {
    path: 'lines-prefill',
    loadChildren: () => import('./app-pages/products-orders/lines-prefill/lines-prefill.routes').then(m => m.getLinesPrefillRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Lines pre-fill',
      permissions: {
        menuAccess: ['Portal.Admin.ProductsAndOrders.LinesPreFill.MenuAccess'],
      },
    },
  },
  {
    path: 'requests',
    loadChildren: () => import('./app-pages/products-orders/requests/requests.routes').then(m => m.getRequestsRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Requests',
      permissions: {
        menuAccess: ['Portal.Admin.ProductsAndOrders.Requests.MenuAccess'],
      },
    },
  },
  {
    path: 'notifications',
    children: [
      { path: '', redirectTo: 'groups', pathMatch: 'full' },
      {
        path: 'groups',
        loadChildren: () =>
          import('./app-pages/notifications/receiver-groups/receiver-groups.routes').then(m => m.getReceiverGroupsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Receiver groups',
          permissions: {
            menuAccess: ['Portal.Admin.Notifications.ReceiverGroups.MenuAccess'],
          },
        },
      },
      {
        path: 'external-templates',
        loadChildren: () =>
          import('./app-pages/notifications/external-templates/external-templates.routes').then(m => m.getExternalTemplatesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'External templates',
          permissions: {
            menuAccess: ['Portal.Admin.Notifications.ExternalTemplates.MenuAccess'],
          },
        },
      },
      {
        path: 'template-groups',
        loadChildren: () =>
          import('./app-pages/notifications/template-groups/template-groups.routes').then(m => m.getTemplateGroupRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Template groups',
          permissions: {
            menuAccess: ['Portal.Admin.Notifications.TemplateGroups.MenuAccess'],
          },
        },
      },
      {
        path: 'templates',
        loadChildren: () => import('./app-pages/notifications/templates/templates.routes').then(m => m.getTemplateRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Templates',
          permissions: {
            menuAccess: ['Portal.Admin.Notifications.Templates.MenuAccess'],
          },
        },
      },
      {
        path: 'logs',
        loadChildren: () => import('./app-pages/notifications/logs/notification-logs.routes').then(m => m.getNotificationLogsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Notification logs',
          permissions: {
            menuAccess: ['Portal.Admin.Notifications.Logs.MenuAccess'],
          },
        },
      },
    ],
  },
  {
    path: 'csp',
    children: [
      { path: '', redirectTo: 'tenants', pathMatch: 'full' },
      {
        path: 'tenants',
        loadChildren: () => import('./app-pages/csp/tenants/tenants.routes').then(m => m.getTenantsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Tenants',
          permissions: {
            menuAccess: ['Portal.Admin.Csp.Tenants.MenuAccess'],
          },
        },
      },
      {
        path: 'license-types',
        loadChildren: () => import('./app-pages/csp/license-types/license-types.routes').then(m => m.getLicenseTypesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'License types',
          permissions: {
            menuAccess: ['Portal.Admin.Csp.LicenseTypes.MenuAccess'],
          },
        },
      },
      {
        path: 'offers',
        loadChildren: () => import('./app-pages/csp/license-offers/license-offers.routes').then(m => m.getLicenseOffersRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Offers',
          permissions: {
            menuAccess: ['Portal.Admin.Csp.LicenseOffers.MenuAccess'],
          },
        },
      },
      {
        path: 'entra-apps',
        loadChildren: () => import('./app-pages/csp/entra-apps/entra-apps.routes').then(m => m.getEntraAppsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Entra apps',
          permissions: {
            menuAccess: ['Portal.Admin.Csp.EntraApps.MenuAccess'],
          },
        },
      },
      {
        path: 'mpn-mappings',
        loadChildren: () => import('./app-pages/csp/mpn-mappings/mpn-mappings.routes').then(m => m.getMpnMappingsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'MPN mappings',
          permissions: {
            menuAccess: ['Portal.Admin.Csp.MpnMappings.MenuAccess'],
          },
        },
      },
    ],
  },
  {
    path: 'customers',
    loadChildren: () => import('./app-pages/customers/customers.routes').then(m => m.getCustomersRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Customers',
      permissions: {
        menuAccess: ['Portal.Admin.Customers.MenuAccess'],
      },
    },
  },
  {
    path: 'orders',
    loadChildren: () => import('./app-pages/products-orders/orders/orders.routes').then(m => m.getOrdersRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Orders',
      permissions: {
        menuAccess: ['Portal.Admin.ProductsAndOrders.Orders.MenuAccess'],
      },
    },
  },
  {
    path: 'monitoring',
    children: [
      { path: '', redirectTo: 'alert-types', pathMatch: 'full' },
      {
        path: 'alert-types',
        loadChildren: () => import('./app-pages/monitoring/alert-types/alert-types.routes').then(m => m.getAlertTypesRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Alert types',
          permissions: {
            menuAccess: ['Portal.Admin.Monitoring.AlertTypes.MenuAccess'],
            oneAtLeast: ['Portal.Admin.Monitoring.AlertTypes.Read'],
          },
        },
      },
      {
        path: 'configured-alerts',
        loadChildren: () =>
          import('./app-pages/monitoring/configured-alerts/configured-alerts.routes').then(m => m.getConfiguredAlertsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Configured alerts',
          permissions: {
            menuAccess: ['Portal.Admin.Monitoring.ConfiguredAlerts.MenuAccess'],
            oneAtLeast: ['Portal.Admin.Monitoring.ConfiguredAlerts.Read'],
          },
        },
      },
      {
        path: 'logs',
        loadChildren: () => import('./app-pages/monitoring/logs/alert-logs.routes').then(m => m.getAlertLogsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Alert logs',
          permissions: {
            menuAccess: ['Portal.Admin.Monitoring.Logs.MenuAccess'],
          },
        },
      },
    ],
  },
  {
    path: 'job-orchestrator',
    children: [
      { path: '', redirectTo: 'workflows', pathMatch: 'full' },
      {
        path: 'workflows',
        loadChildren: () => import('./app-pages/job-orchestrator/workflows/workflows.routes').then(m => m.getWorkflowsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Workflows',
          permissions: {
            menuAccess: ['Portal.Admin.JobOrchestrator.Workflows.MenuAccess'],
          },
        },
      },
      {
        path: 'operations',
        loadChildren: () => import('./app-pages/job-orchestrator/operations/operations.routes').then(m => m.getOperationsRoutes()),
        canActivate: [PermissionsGuard],
        data: {
          pageTitle: 'Operations',
          permissions: {
            menuAccess: ['Portal.Admin.JobOrchestrator.Operations.MenuAccess'],
          },
        },
      },
    ],
  },
  {
    path: 'empowered-licenses',
    loadChildren: () =>
      import('./app-pages/products-orders/empowered-licenses/empowered-licenses.routes').then(m => m.getEmpoweredLicensesRoutes()),
    canActivate: [PermissionsGuard],
    data: {
      pageTitle: 'Empowered licenses',
      permissions: {
        menuAccess: ['Portal.Admin.ProductsAndOrders.EmpoweredLicenses.MenuAccess'],
      },
    },
  },
];

const routes: AuthorizedRoute[] = [
  {
    path: 'marketing/news-articles/:articleId/preview',
    component: NewsArticlePreviewComponent,
    canActivate: [AuthGuard, GuidGuard, PermissionsGuard],
    data: {
      pageTitle: 'News article preview',
      permissions: {
        oneAtLeast: [
          'Portal.Admin.Marketing.NewsArticles.Preview.ActionAccess',
          'Portal.Admin.Marketing.Communities.NewsArticles.Preview.ActionAccess',
        ],
      },
    },
  },
  {
    path: 'marketing/recordings/:recordingId/preview',
    component: RecordingPreviewComponent,
    canActivate: [AuthGuard, GuidGuard, PermissionsGuard],
    data: {
      pageTitle: 'Recording preview',
      permissions: {
        oneAtLeast: [
          'Portal.Admin.Marketing.Recordings.Preview.ActionAccess',
          'Portal.Admin.Marketing.Communities.Recordings.Preview.ActionAccess',
        ],
      },
    },
  },
  {
    path: '',
    component: AuthenticatedTemplateComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./app-pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
        data: {
          pageTitle: 'Dashboard',
        },
      },
      ...authorizedRoutes,
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: '**', redirectTo: 'page/page-not-found' },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
